import {
  Button,
  Card,
  Container,
  Section,
  Stack,
  useDialogProvider,
} from "@storoey/sui";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/solid-router";
import { createSignal, For, onMount } from "solid-js";
import { createStore } from "solid-js/store";
import { database } from "../../../../../backend/database";
import {
  PhotoDto,
  RecordingDto,
  TaskDto,
} from "../../../../../backend/models/dto/Item.dto";
import { SectionBreadcrumbs } from "../../../../../components/SectionBreadcrumbs/SectionBreadcrumbs";
import { TaskButton } from "../../../../../components/TaskButton/TaskButton";
import { useNavbar } from "../../../../../contexts/RootContext";
import { AudioDialog } from "../../../../../dialogs/Task/Audio.dialog";
import { PhotoDialog } from "../../../../../dialogs/Task/Photo.dialog";
import styles from "./index.module.scss";

export const Route = createFileRoute(
  "/history/$formId/$categoryId/$sectionId/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const loaderData = Route.parentRoute.useLoaderData();
  const [items, setItems] = createStore(loaderData().items);

  const [dialogTaskId, setDialogTaskId] = createSignal<number | null>(null);
  const [dialogItemId, setDialogItemId] = createSignal<number | null>(null);
  const [dialogRecordings, setDialogRecordings] = createSignal<
    RecordingDto[] | null
  >(null);
  const [dialogPhotoss, setDialogPhotos] = createSignal<PhotoDto[] | null>(
    null
  );
  const recordingDialog = useDialogProvider();
  const photoDialog = useDialogProvider();

  const router = useRouter();
  const { setTitle } = useNavbar();
  const navigate = useNavigate();

  onMount(() => {
    setTitle(loaderData().section.title);
  });

  const handleUpdateTask = async (
    itemId: number,
    data: {
      id: number;
      status: TaskDto["status"];
      overrideId?: number;
    }
  ) => {
    const updateBody: {
      status: TaskDto["status"];
      overrideId: TaskDto["overrideId"];
    } = {
      status: data.status,
      overrideId: data.overrideId,
    };

    if (data.overrideId) {
      if (data.status === null) {
        await database.taskOverrides.delete(data.overrideId);
        updateBody.overrideId = undefined;
      } else {
        await database.taskOverrides.update(data.overrideId, {
          status: data.status,
        });
      }
    } else {
      const taskOverrideId = await database.taskOverrides.add({
        formId: loaderData().formId,
        status: data.status,
        taskId: data.id,
      });

      updateBody["overrideId"] = taskOverrideId;
    }

    setItems(
      (item) => item.id === itemId,
      "tasks",
      (task) => task.id === data.id,
      updateBody
    );

    router.invalidate();
  };

  const handleDialogGone = async () => {
    setDialogTaskId(null);
    setDialogItemId(null);
    setDialogRecordings(null);
  };

  const handleShowRecordingDialog = (itemId: number, taskId: number) => {
    setDialogTaskId(taskId);
    setDialogItemId(itemId);
    setDialogRecordings(
      items.find((x) => x.id === itemId)?.tasks.find((x) => x.id === taskId)
        ?.recordings ?? []
    );

    recordingDialog.open();
  };

  const handleNewRecording = async (blob: Blob) => {
    const recording = {
      blob: blob,
      date: new Date(),
      formId: loaderData().formId,
      taskId: dialogTaskId()!,
    };

    const blobId = await database.recordings.add(recording);

    const dto: RecordingDto = {
      id: blobId,
      ...recording,
    };

    setItems(
      (item) => item.id === dialogItemId(),
      "tasks",
      (task) => task.id === dialogTaskId(),
      "recordings",
      (recordings) => [dto, ...recordings]
    );

    setDialogRecordings((recordings) => [dto, ...(recordings ?? [])]);
  };

  const handleRecordingDelete = async (id: RecordingDto["id"]) => {
    await database.recordings.delete(id);

    setItems(
      (item) => item.id === dialogItemId(),
      "tasks",
      (task) => task.id === dialogTaskId(),
      "recordings",
      (recordings) => recordings.filter((x) => x.id !== id)
    );

    setDialogRecordings((recordings) =>
      [...(recordings ?? [])].filter((x) => x.id !== id)
    );
  };

  const handleShowPhotoDialog = (itemId: number, taskId: number) => {
    setDialogTaskId(taskId);
    setDialogItemId(itemId);
    setDialogPhotos(
      items.find((x) => x.id === itemId)?.tasks.find((x) => x.id === taskId)
        ?.photos ?? []
    );

    photoDialog.open();
  };

  const handleNewPhoto = async (file: File) => {
    const photo = {
      file: file,
      date: new Date(),
      formId: loaderData().formId,
      taskId: dialogTaskId()!,
    };

    const blobId = await database.photos.add(photo);

    const dto: PhotoDto = {
      id: blobId,
      ...photo,
    };

    setItems(
      (item) => item.id === dialogItemId(),
      "tasks",
      (task) => task.id === dialogTaskId(),
      "photos",
      (photos) => [dto, ...photos]
    );

    setDialogPhotos((photos) => [dto, ...(photos ?? [])]);
  };

  const handlePhotoDelete = async (id: PhotoDto["id"]) => {
    await database.photos.delete(id);

    setItems(
      (item) => item.id === dialogItemId(),
      "tasks",
      (task) => task.id === dialogTaskId(),
      "photos",
      (photos) => photos.filter((x) => x.id !== id)
    );

    setDialogPhotos((photos) => [...(photos ?? [])].filter((x) => x.id !== id));
  };

  return (
    <>
      <Section
        padding="lg"
        breadcrumbs={
          <SectionBreadcrumbs
            items={[
              {
                title: "Hjem",
                path: "/",
              },
              {
                title: "Historie",
                path: "/history",
              },
              {
                title: loaderData().formName,
                path: `/history/` + loaderData().formId,
              },
              {
                title: loaderData().categoryName,
                path:
                  `/history/` +
                  loaderData().formId +
                  "/" +
                  loaderData().categoryId,
              },
              {
                title: loaderData().section.title,
              },
            ]}
          />
        }
      >
        <Container>
          <Stack>
            <For each={items}>
              {(item) => (
                <Card elevation={200}>
                  <Card.Content>
                    <Card.Title>{item.title}</Card.Title>
                  </Card.Content>
                  <Card.Actions>
                    <Stack gap="16px">
                      <For each={item.tasks}>
                        {(task) => (
                          <TaskButton
                            task={task}
                            onUpdate={(data) => handleUpdateTask(item.id, data)}
                            onShowRecording={(taskId) =>
                              handleShowRecordingDialog(item.id, taskId)
                            }
                            onShowPhoto={(taskId) =>
                              handleShowPhotoDialog(item.id, taskId)
                            }
                          />
                        )}
                      </For>
                    </Stack>
                  </Card.Actions>
                </Card>
              )}
            </For>
          </Stack>

          <div class={styles["bottom-actions"]}>
            <div class={styles["actions"]}>
              <Button
                color="primary"
                onClick={() =>
                  navigate({
                    to:
                      "/history/" +
                      loaderData().formId +
                      "/" +
                      loaderData().categoryId +
                      "/" +
                      loaderData().section.id +
                      "/results",
                  })
                }
              >
                Resultat
              </Button>
            </div>
          </div>
        </Container>
      </Section>
      <AudioDialog
        recordings={dialogRecordings()}
        isOpen={recordingDialog.isOpen}
        close={recordingDialog.close}
        open={recordingDialog.open}
        onNewRecording={handleNewRecording}
        onDelete={handleRecordingDelete}
        onCleanup={handleDialogGone}
      />

      <PhotoDialog
        photos={dialogPhotoss()}
        isOpen={photoDialog.isOpen}
        close={photoDialog.close}
        open={photoDialog.open}
        onNewPhoto={handleNewPhoto}
        onDelete={handlePhotoDelete}
        onCleanup={handleDialogGone}
      />
    </>
  );
}
